import { useRef, useCallback, useState, useEffect } from 'react';
import appLogger from '@/services/logger';

interface UseAudioPreviewReturn {
  currentlyPlayingId: string | null;
  isPlaying: (trackId: string) => boolean;
  playTrack: (trackId: string, url: string) => void;
  pauseTrack: () => void;
  stopTrack: () => void;
}

/**
 * Hook for managing audio preview playback
 * Ensures only one track plays at a time and handles cleanup
 */
export const useAudioPreview = (): UseAudioPreviewReturn => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [currentlyPlayingId, setCurrentlyPlayingId] = useState<string | null>(null);

  // Cleanup audio element on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = '';
        audioRef.current = null;
      }
    };
  }, []);

  const stopTrack = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current.src = '';
    }
    setCurrentlyPlayingId(null);
  }, []);

  const pauseTrack = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
    }
    setCurrentlyPlayingId(null);
  }, []);

  const playTrack = useCallback(
    (trackId: string, url: string) => {
      // Stop previous track if playing
      if (audioRef.current && currentlyPlayingId !== trackId) {
        audioRef.current.pause();
        audioRef.current.src = '';
      }

      // If clicking the same track that's playing, pause it
      if (currentlyPlayingId === trackId && audioRef.current) {
        pauseTrack();
        return;
      }

      // Create new audio element if needed
      if (!audioRef.current) {
        audioRef.current = new Audio();
        audioRef.current.preload = 'metadata';

        // Handle audio errors gracefully
        audioRef.current.addEventListener('error', () => {
          appLogger.error('Failed to load audio preview');
          stopTrack();
        });

        // Clean up when audio ends
        audioRef.current.addEventListener('ended', () => {
          stopTrack();
        });
      }

      // Set source and play
      audioRef.current.src = url;
      audioRef.current.play().catch(error => {
        appLogger.error('Failed to play audio preview', error);
        stopTrack();
      });

      setCurrentlyPlayingId(trackId);
    },
    [currentlyPlayingId, pauseTrack, stopTrack]
  );

  const isPlaying = useCallback(
    (trackId: string) => {
      return currentlyPlayingId === trackId && audioRef.current?.paused === false;
    },
    [currentlyPlayingId]
  );

  return {
    currentlyPlayingId,
    isPlaying,
    playTrack,
    pauseTrack,
    stopTrack
  };
};
